
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import StatusBadge from './StatusBadge';
import { UserCog, Trash2 } from 'lucide-react';

interface Employee {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  is_active: boolean;
}

interface EmployeesTableProps {
  employees: Employee[];
  onRemoveEmployee: (id: string) => void;
  onToggleActive?: (id: string, isActive: boolean) => void;
  isProcessing: boolean;
}

const EmployeesTable: React.FC<EmployeesTableProps> = ({
  employees,
  onRemoveEmployee,
  onToggleActive,
  isProcessing,
}) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map((employee) => (
            <TableRow key={employee.id} className={!employee.is_active ? "bg-gray-50 opacity-70" : ""}>
              <TableCell className="font-medium">{employee.full_name}</TableCell>
              <TableCell>{employee.email}</TableCell>
              <TableCell>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  <UserCog className="h-3 w-3 mr-1" />
                  {employee.role}
                </Badge>
              </TableCell>
              <TableCell>
                <StatusBadge status={employee.status} />
              </TableCell>
              <TableCell>
                {onToggleActive && (
                  <Switch 
                    checked={employee.is_active} 
                    onCheckedChange={() => onToggleActive(employee.id, !employee.is_active)}
                    disabled={isProcessing}
                  />
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  onClick={() => onRemoveEmployee(employee.id)}
                  size="sm"
                  variant="ghost"
                  className="h-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Remove</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {employees.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                No employees found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default EmployeesTable;
